import './utils/mixpanelWrapper.js';
import './tinuiti.scss';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { StrictMode } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App.js';
import { ErrorBoundary } from './Components/ErrorBoundary/ErrorBoundary';
import { UNKNOWN_ERROR_MESSAGE } from './Components/ErrorBoundary/ErrorFallbackMessage';
import { Page } from './Components/Page/Page';
import { reduxStore } from './redux';
import { dispatchError } from './redux/modals';
import { ConnectRouteMode, getConnectRouteMode } from './utils/auth/oauth-client';
import { redactRumEvent } from './utils/auth/rum-redact';
import { snapshotStartupCoverage } from './utils/cypress-startup-coverage';
import { ErrorReference, classifyModalError, reportWithReference } from './utils/error-reference';
import { FetchError } from './utils/fetch-error';
import { UntraceableError } from './utils/untraceable-error';

// Shown for any uncaught client-side crash below (render errors outside a `<Page>`, event-handler
// exceptions, unhandled promise rejections). Whether it carries an ID, and which kind, comes from
// classifying the raw error (utils/error-reference.ts): a real app bug gets a stable FE- ID, an
// uncaught FetchError keeps its backend requestId, and a network/gateway failure gets none.
const showReference = (message: string, { source, id }: ErrorReference) =>
  dispatchError(reduxStore.dispatch, { message, errorSource: source, referenceId: id });
const surfaceUnknownClientError = (error: unknown) =>
  showReference(UNKNOWN_ERROR_MESSAGE, classifyModalError({ reportError: error }));
// For call sites that haven't already reported the error themselves (the ErrorBoundary below
// reports on every catch unconditionally, so its onError only needs to surface the modal).
const reportUnknownClientError = (error: unknown, operation: string) =>
  showReference(UNKNOWN_ERROR_MESSAGE, reportWithReference(error, operation));

datadogRum.init({
  applicationId: import.meta.env.VITE_DD_RUM_APPLICATION_ID || '',
  clientToken: import.meta.env.VITE_DD_RUM_CLIENT_TOKEN || '',
  site: import.meta.env.VITE_DD_SITE || '',
  service: import.meta.env.VITE_DD_SERVICE || '',
  env: import.meta.env.VITE_DD_ENV || '',
  version: import.meta.env.VITE_DD_VERSION || '',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  plugins: [reactPlugin({ router: true })],
  beforeSend: event => {
    redactRumEvent(event);
    return true;
  }
});

// Session replay records window.location.href outside beforeSend, so the /connect query (sign-in
// transaction, login hint) would reach Datadog unredacted. Must run after init: App's module-level
// route check runs before init, where the call would be a no-op.
if (getConnectRouteMode(window.location) === ConnectRouteMode.CONNECT) {
  datadogRum.stopSessionReplayRecording();
}

datadogRum.setGlobalContextProperty('amplify_build', import.meta.env.VITE_AWS_JOB_ID || 'n/a');

// This is all related to CORS issues from running bpm-web in iframe.
// We can potentially get rid of this once we stop doing that.
if (!document.domain.endsWith('amplifyapp.com')) {
  try {
    document.domain = import.meta.env.VITE_DOMAIN;
  } catch (e) {
    // Setting document.domain seems to fail when loading our app in an iFrame, which I don't care about.
    console.error(e.message);
  }
}

// Safety net for the ~85 useQuery call sites that never check isError (see DEV-24579): any query
// that fails without its own error handling still surfaces something instead of silently looking
// like it's still loading. A query already showing its own error (e.g. via useSetError in a
// useEffect keyed on isError) opts out with `meta: { suppressGlobalError: true }` so it isn't
// double-alerted. Only FetchError and UntraceableError messages are safe to show; anything else gets generic text.
// `reportError` lets setError (redux/modals.ts) report once and apply the friendly mapping.
const reportUnhandledQueryError = (error: unknown, operationTag: string) =>
  dispatchError(reduxStore.dispatch, {
    message:
      error instanceof FetchError || error instanceof UntraceableError
        ? error.message
        : 'Something went wrong loading this data.',
    reportError: error,
    operationTag
  });

// Closes the one gap no Error Boundary placement can close: boundaries only ever see render-phase
// errors, never an event-handler exception or an unhandled promise rejection. Datadog RUM's own
// automatic instrumentation independently listens for these same two events regardless of this
// code, so a real crash will show up twice in Datadog -- once automatic/untagged, once
// manual/tagged (operation, errorSource, errorId) via reportWithReference. An `error` event with
// no `event.error` (a ResizeObserver loop notification, a cross-origin "Script error.") is not an
// app bug and has nothing to trace, so it gets no modal.
window.addEventListener('error', e => {
  if (!e.error) return;
  reportUnknownClientError(e.error, 'window.onerror');
});
window.addEventListener('unhandledrejection', e => {
  reportUnknownClientError(e.reason, 'unhandledrejection');
});

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.suppressGlobalError) return;
      reportUnhandledQueryError(error, String(query.queryKey[0] ?? 'unknown-query'));
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache kept for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on component mount if data exists
      retry: 1 // Only retry failed requests once
    }
  }
});

declare global {
  interface Window {
    queryClient?: QueryClient;
  }
}

// Cypress only: specs that move a fake clock read it to wait until a poll request has finished.
if (window.Cypress) {
  window.queryClient = queryClient;
}

// Cypress builds only: record which functions ran during module start-up. Keep this before
// render(), so the snapshot holds no hits from rendering.
snapshotStartupCoverage();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={reduxStore}>
      <QueryClientProvider client={queryClient}>
        <DndProvider backend={HTML5Backend}>
          {/* `<Page onlyModals />` is the one persistent, always-mounted renderer of the shared
              error/are-you-sure modal state -- it needs no router context (confirmed: every hook
              Page calls is redux-only), so it survives even if <App/>'s subtree (which owns the
              router) is replaced by the boundary's fallback below. */}
          <ErrorBoundary fallback={null} onError={surfaceUnknownClientError}>
            <App />
          </ErrorBoundary>
          <Page onlyModals />
        </DndProvider>
      </QueryClientProvider>
    </Provider>
  </StrictMode>
);
